/**
 * CancellationPolicySelector Component
 *
 * Allows guides to select or customize the cancellation policy
 * for their experiences. Shows standard policy templates and
 * supports custom tier configuration.
 *
 * API Endpoints:
 *   GET  /api/v1/cancellation-policies/templates
 *   GET  /api/v1/cancellation-policies/experiences/:experienceId
 *   PUT  /api/v1/cancellation-policies/experiences/:experienceId
 *
 * Story: NEW-001-FE - Cancellation Policy Management (Guide UI)
 * MVP: 1.5.2 - Booking Payment Domain Model
 */

import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import apiRequest from '../../services/api';
import { Shield, Check, ChevronDown, ChevronUp, Plus, Trash2, AlertCircle } from 'lucide-react';

const POLICY_DESCRIPTIONS = {
  flexible: 'Best for attracting new customers. Generous refund terms build trust.',
  moderate: 'Balanced protection for both you and your customers.',
  strict: 'Maximum protection for your schedule. Best for high-demand experiences.',
};

export default function CancellationPolicySelector({ experienceId }) {
  const [templates, setTemplates] = useState([]);
  const [currentPolicy, setCurrentPolicy] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [customTiers, setCustomTiers] = useState([
    { daysThreshold: 14, refundPercentage: 1.0 },
    { daysThreshold: 7, refundPercentage: 0.5 },
    { daysThreshold: 0, refundPercentage: 0 },
  ]);
  const [showCustom, setShowCustom] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  // Fetch templates and current policy
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [templatesRes, policyRes] = await Promise.all([
        apiRequest('/api/v1/cancellation-policies/templates'),
        apiRequest(`/api/v1/cancellation-policies/experiences/${experienceId}`),
      ]);

      if (templatesRes.success) {
        setTemplates(templatesRes.data);
      }

      if (policyRes.success && policyRes.data) {
        setCurrentPolicy(policyRes.data);
        setSelectedType(policyRes.data.policyType || policyRes.data.policy_type);
        if ((policyRes.data.policyType || policyRes.data.policy_type) === 'custom' && policyRes.data.tiers) {
          setCustomTiers(policyRes.data.tiers);
          setShowCustom(true);
        }
      }
    } catch (err) {
      setError(err.message || 'Failed to load cancellation policies');
    } finally {
      setLoading(false);
    }
  }, [experienceId]);

  useEffect(() => {
    if (experienceId) {
      fetchData();
    }
  }, [experienceId, fetchData]);

  const handleSelectPolicy = (type) => {
    setSelectedType(type);
    if (type === 'custom') {
      setShowCustom(true);
    } else {
      setShowCustom(false);
    }
    setSuccessMsg(null);
  };

  const handleAddTier = () => {
    const maxDays = Math.max(...customTiers.map((t) => t.daysThreshold), 0);
    setCustomTiers([
      ...customTiers,
      { daysThreshold: maxDays + 7, refundPercentage: 0.5 },
    ]);
  };

  const handleRemoveTier = (index) => {
    // Don't allow removing the base tier (daysThreshold = 0)
    if (customTiers[index].daysThreshold === 0) {return;}
    setCustomTiers(customTiers.filter((_, i) => i !== index));
  };

  const handleTierChange = (index, field, value) => {
    const updated = [...customTiers];
    if (field === 'daysThreshold') {
      updated[index] = { ...updated[index], daysThreshold: parseInt(value, 10) || 0 };
    } else if (field === 'refundPercentage') {
      // Input is 0-100, store as 0-1
      updated[index] = { ...updated[index], refundPercentage: Math.min(1, Math.max(0, parseFloat(value) / 100 || 0)) };
    }
    setCustomTiers(updated);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccessMsg(null);

      const body = {
        policyType: selectedType,
      };

      if (selectedType === 'custom') {
        // Sort tiers descending by daysThreshold for validation
        const sortedTiers = [...customTiers].sort((a, b) => b.daysThreshold - a.daysThreshold);
        body.customTiers = sortedTiers;
      }

      const response = await apiRequest(
        `/api/v1/cancellation-policies/experiences/${experienceId}`,
        {
          method: 'PUT',
          body: JSON.stringify(body),
        }
      );

      if (response.success) {
        setCurrentPolicy(response.data);
        setSuccessMsg('Cancellation policy saved successfully');
        setTimeout(() => setSuccessMsg(null), 5000);
      }
    } catch (err) {
      setError(err.message || 'Failed to save cancellation policy');
      setTimeout(() => setError(null), 5000);
    } finally {
      setSaving(false);
    }
  };

  const formatTiers = (tiers) => {
    if (!tiers || !Array.isArray(tiers)) {return [];}
    return [...tiers].sort((a, b) => b.daysThreshold - a.daysThreshold);
  };

  const isChanged = selectedType !== (currentPolicy?.policyType || currentPolicy?.policy_type);

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center space-x-3 mb-4">
          <Shield className="w-5 h-5 text-brand-primary" />
          <h3 className="text-lg font-semibold text-gray-900">Cancellation Policy</h3>
        </div>
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
          <span className="ml-3 text-gray-600">Loading policies...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      {/* Header */}
      <div className="flex items-center space-x-3 mb-6">
        <Shield className="w-5 h-5 text-brand-primary" />
        <h3 className="text-lg font-semibold text-gray-900">Cancellation Policy</h3>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-2">
          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
          <span className="text-sm text-red-800">{error}</span>
        </div>
      )}

      {/* Success Message */}
      {successMsg && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center space-x-2">
          <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
          <span className="text-sm text-green-800">{successMsg}</span>
        </div>
      )}

      {/* Policy Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {templates.map((template) => {
          const type = template.policyType || template.policy_type;
          const isSelected = selectedType === type;
          const tiers = formatTiers(template.tiers);

          return (
            <button
              key={type}
              onClick={() => handleSelectPolicy(type)}
              className={`text-left p-4 rounded-lg border-2 transition-all ${
                isSelected
                  ? 'border-brand-primary bg-green-50'
                  : 'border-gray-200 hover:border-gray-300 bg-white'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-semibold text-gray-900 capitalize">{type}</h4>
                {type === 'moderate' && (
                  <span className="text-xs font-medium bg-brand-primary text-white px-2 py-0.5 rounded-full">
                    Recommended
                  </span>
                )}
                {isSelected && (
                  <Check className="w-5 h-5 text-brand-primary flex-shrink-0" />
                )}
              </div>

              <p className="text-xs text-gray-500 mb-3">{POLICY_DESCRIPTIONS[type]}</p>

              {/* Tier List */}
              <div className="space-y-1.5">
                {tiers.map((tier, i) => (
                  <div key={i} className="flex items-center text-xs">
                    <div
                      className={`w-2 h-2 rounded-full mr-2 flex-shrink-0 ${
                        tier.refundPercentage >= 0.75
                          ? 'bg-green-500'
                          : tier.refundPercentage >= 0.25
                            ? 'bg-yellow-500'
                            : 'bg-red-500'
                      }`}
                    />
                    <span className="text-gray-700">
                      {tier.daysThreshold > 0
                        ? `${tier.daysThreshold}+ days: ${Math.round(tier.refundPercentage * 100)}% refund`
                        : `Under ${tiers[i - 1]?.daysThreshold || 0} days: ${Math.round(tier.refundPercentage * 100)}% refund`}
                    </span>
                  </div>
                ))}
              </div>
            </button>
          );
        })}
      </div>

      {/* Custom Policy Toggle */}
      <div className="border-t border-gray-200 pt-4 mb-4">
        <button
          onClick={() => {
            if (!showCustom) {
              handleSelectPolicy('custom');
            } else if (selectedType === 'custom') {
              // Collapse custom section and deselect
              setShowCustom(false);
              setSelectedType(currentPolicy?.policyType || currentPolicy?.policy_type || 'moderate');
            } else {
              setShowCustom(false);
            }
          }}
          className="flex items-center justify-between w-full text-left"
        >
          <div className="flex items-center space-x-2">
            <span className="font-medium text-gray-900">Custom Policy</span>
            {selectedType === 'custom' && (
              <span className="text-xs font-medium bg-brand-primary text-white px-2 py-0.5 rounded-full">
                Active
              </span>
            )}
          </div>
          {showCustom ? (
            <ChevronUp className="w-4 h-4 text-gray-500" />
          ) : (
            <ChevronDown className="w-4 h-4 text-gray-500" />
          )}
        </button>
      </div>

      {/* Custom Tier Editor */}
      {showCustom && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg space-y-3">
          <p className="text-xs text-gray-500 mb-3">
            Define custom refund tiers. Each tier specifies how many days before the trip and what refund percentage applies.
            Must include a base tier (0 days).
          </p>

          {[...customTiers]
            .sort((a, b) => b.daysThreshold - a.daysThreshold)
            .map((tier, sortedIdx) => {
              // Find original index for mutations
              const origIdx = customTiers.findIndex(
                (t) => t.daysThreshold === tier.daysThreshold && t.refundPercentage === tier.refundPercentage
              );
              return (
                <div key={sortedIdx} className="flex items-center gap-3">
                  <div className="flex-1">
                    <label className="block text-xs text-gray-500 mb-1">Days Before Trip</label>
                    <input
                      type="number"
                      min="0"
                      value={tier.daysThreshold}
                      onChange={(e) => handleTierChange(origIdx, 'daysThreshold', e.target.value)}
                      disabled={tier.daysThreshold === 0}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md disabled:bg-gray-100"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="block text-xs text-gray-500 mb-1">Refund %</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={Math.round(tier.refundPercentage * 100)}
                      onChange={(e) => handleTierChange(origIdx, 'refundPercentage', e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md"
                    />
                  </div>
                  <div className="pt-5">
                    <button
                      onClick={() => handleRemoveTier(origIdx)}
                      disabled={tier.daysThreshold === 0}
                      className="p-1.5 text-gray-400 hover:text-red-600 disabled:opacity-30 disabled:cursor-not-allowed"
                      title={tier.daysThreshold === 0 ? 'Base tier cannot be removed' : 'Remove tier'}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}

          <button
            onClick={handleAddTier}
            className="flex items-center space-x-1 text-sm text-brand-primary hover:text-brand-dark"
          >
            <Plus className="w-4 h-4" />
            <span>Add Tier</span>
          </button>
        </div>
      )}

      {/* Save Button */}
      <button
        onClick={handleSave}
        disabled={saving || (!isChanged && selectedType !== 'custom')}
        className="w-full sm:w-auto px-6 py-2.5 bg-brand-primary text-white font-medium rounded-lg hover:bg-brand-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {saving ? 'Saving...' : 'Save Policy'}
      </button>
    </div>
  );
}

CancellationPolicySelector.propTypes = {
  experienceId: PropTypes.string.isRequired,
};
